import React, { useState, useEffect } from 'react';
import { 
  FaCogs, 
  FaPlus, 
  FaTrash, 
  FaBuilding, 
  FaProjectDiagram, 
  FaLayerGroup, 
  FaSitemap, 
  FaWallet, 
  FaChartBar, 
  FaMoneyBillWave, 
  FaMapMarkerAlt,
  FaList,
  FaSpinner
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import { rootUrl } from '../../helpers';
import FormGenerator from '../../components/formGenerator';
import Modal from '../../components/Modal';
import { useAuth } from '../../context/auth-context';

export default function Settings() {
  const auth = useAuth();
  const [modal, setModal] = useState({ title: '', open: false, body: '', size: 'sm' });
  const [activeTab, setActiveTab] = useState(0); // To manage tabs

  let type = '';

  const [budget, setBudget] = useState([]);
  const [banks, setBanks] = useState([]);
  const [coa, setCoa] = useState([]);
  const [dept, setDept] = useState([]);
  const [program, setProgram] = useState([]);
  const [subProgram, setSubProgram] = useState([]);
  const [units, setUnits] = useState([]);
  const [councils, setCouncil] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [center, setCenter] = useState([]);
  const [output, setOutput] = useState([]); 
  const [requiresPvApproval, setRequiresPvApproval] = useState(true);
  const [savingApprovalSetting, setSavingApprovalSetting] = useState(false);

  const handleClose = () => {
    setModal({ ...modal, open: false });
  };

  const fetchData = async () => {
    try {
      const { data } = await axios.get(rootUrl(`/getProjects/${auth.user.id}`));
      setBudget(data.data.budget);
      setCoa(data.data.coa);
      setDept(data.data.dept);
      setProgram(data.data.program);
      setSubProgram(data.data.subProgram);
      setOutput(data.data.subProgram);
      setUnits(data.data.units);
      setCouncil(data.data.councils);
      setBanks(data.data.banks);
      setExpenses(data.data.expenses);
      setCenter(data.data.center);

      const currentCouncil = (data.data.councils || []).find(
        (c) => String(c.code) === String(auth.user.cid)
      );
      if (currentCouncil && currentCouncil.pv_requires_approval !== undefined) {
        setRequiresPvApproval(Number(currentCouncil.pv_requires_approval) === 1);
      } else {
        setRequiresPvApproval(true);
      }
    } catch (e) {
      toast.error(e?.message);
    }
  };

  useEffect(() => {
    if (auth.user != null) {
      fetchData();
    }
  }, []);

  const submitSetting = async (data) => {
    data['type'] = type;
    data['c_id'] = auth.user.cid;
    try {
      const response = await axios.post(rootUrl('/settings'), data);
      if (response.data.status === 200) {
        toast.success('Submitted successfully');
        fetchData();
        setModal({ ...modal, open: false });
      } else {
        toast.error(response.data.message);
      }
    } catch (e) {
      toast.error(e?.message);
    }
  };

  const addSett = (name) => {
    type = name;
    let fields = [{ type: 'text', name: 'name', label: 'Name' }];

    switch (name) {
      case 'council':
        fields = [{ type: 'text', name: 'code', label: 'Code' }, { type: 'text', name: 'name', label: 'Name' }];
        break;
      case 'bank':
          fields = [{ type: 'text', name: 'code', label: 'Code' }, { type: 'text', name: 'account_no', label: 'Account #' }, { type: 'text', name: 'name', label: 'Name' }];
          break;
      case 'expense':
          fields = [{ type: 'text', name: 'name', label: 'Name' }];
          break;
      case 'center':
            fields = [{ type: 'text', name: 'collection_name', label: 'Name' }];
            break;
              
      case 'department':
        fields = [{ type: 'text', name: 'id', label: 'Id' }, { type: 'text', name: 'name', label: 'Name' }];
        break;
      case 'program':
        fields = [{ type: 'text', name: 'programCode', label: 'Code' }, { type: 'text', name: 'programName', label: 'Name' }];
        break;
      case 'sub':
        fields = [
          { type: 'text', name: 'subProgramCode', label: 'Code' },
          { type: 'text', name: 'name', label: 'Name' },
          {
            type: 'select',
            name: 'programCode',
            label: 'Program',
            required: true,
            options: program.map((res) => ({ value: res.programCode, label: res.name })),
          },
        ];
        break;
      case 'unit':
        fields = [
          { type: 'text', name: 'name', label: 'Name' },
          {
            type: 'select',
            name: 'departmntCode',
            label: 'Department',
            required: true,
            options: dept.map((res) => ({ value: res.id, label: res.name })),
          },
        ];
        break;
      case 'output':
        fields = [
            { type: 'text', name: 'indicators', label: 'Indicator' },
            { type: 'text', name: 'keyIndicators', label: 'Key Indicator' },
            {
              type: 'select',
              name: 'programCode',
              label: 'Program',
              required: true,
              options: program.map((res) => ({ value: res.programCode, label: res.name })),
            },
          ];
        break;
      default:
        break;
    }

    setModal({
      title: 'Add ' + name,
      open: true,
      size: 'sm',
      body: <FormGenerator fields={fields} onSubmit={submitSetting} />,
    });
  };

  const savePaymentApprovalSetting = async () => {
    setSavingApprovalSetting(true);
    try {
      const response = await axios.post(rootUrl('/updateCouncilPaymentApproval'), {
        cid: auth.user.cid,
        uid: auth.user.id,
        requiresApproval: requiresPvApproval ? 1 : 0,
      });

      if (response.data.status === 200) {
        toast.success('Payment approval setting saved');
        fetchData();
      } else {
        toast.error(response.data.message || 'Failed to save setting');
      }
    } catch (e) {
      toast.error(e?.message);
    } finally {
      setSavingApprovalSetting(false);
    }
  };

  const tabData = [
    // { name: 'Councils', list: councils, addFunc: 'council', icon: FaBuilding },
    { name: 'Departments', list: dept, addFunc: 'department', icon: FaBuilding },
    { name: 'Programs', list: program, addFunc: 'program', icon: FaProjectDiagram },
    { name: 'SubPrograms', list: subProgram, addFunc: 'sub', icon: FaLayerGroup },
    { name: 'Units', list: units, addFunc: 'unit', icon: FaSitemap },
    { name: 'Banks', list: banks, addFunc: 'bank', icon: FaWallet },
    { name: 'Outputs', list: output, addFunc: 'output', icon: FaChartBar },
    { name: 'Expense Type', list: expenses, addFunc: 'expense', icon: FaMoneyBillWave },
    { name: 'Collection Center', list: center, addFunc: 'center', icon: FaMapMarkerAlt },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-800 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 rounded-xl p-3">
            <FaCogs className="text-2xl" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">System Settings</h1>
            <p className="text-blue-100 text-sm mt-1">Configure departments, programs, banks, and more</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <h3 className="font-bold text-lg text-gray-900">Payment Voucher Workflow</h3>
        <p className="text-sm text-gray-600 mt-1">
          Configure whether new payment vouchers should pass through online approval stages or post directly.
        </p>

        <div className="mt-4 flex flex-col gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="pvApprovalMode"
              checked={requiresPvApproval}
              onChange={() => setRequiresPvApproval(true)}
            />
            Use online approval
          </label>
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="radio"
              name="pvApprovalMode"
              checked={!requiresPvApproval}
              onChange={() => setRequiresPvApproval(false)}
            />
            Skip online approval
          </label>
        </div>

        <div className="mt-4">
          <button
            onClick={savePaymentApprovalSetting}
            disabled={savingApprovalSetting}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-950 to-blue-800 text-white rounded-lg hover:shadow-lg transition-all font-medium disabled:opacity-60"
          >
            {savingApprovalSetting ? <FaSpinner className="animate-spin" /> : null}
            Save workflow setting
          </button>
        </div>
      </div>

      {/* Custom Tab Navigation */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="flex flex-wrap gap-2 p-4 border-b border-gray-200 bg-gray-50">
          {tabData.map((tab, index) => {
            const Icon = tab.icon || FaList;
            return (
              <button
                key={index}
                className={`
                  flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all
                  ${activeTab === index
                    ? 'bg-gradient-to-r from-blue-950 to-blue-800 text-white shadow-md scale-105'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }
                `}
                onClick={() => setActiveTab(index)}
              >
                <Icon className={activeTab === index ? 'text-white' : 'text-gray-500'} />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {tabData.map((tab, index) => {
            const Icon = tab.icon || FaList;
            return (
              <div key={index} className={`${activeTab === index ? 'block' : 'hidden'}`}>
                <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                  {/* Tab Header */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className="bg-blue-100 rounded-lg p-2">
                          <Icon className="text-blue-600 text-xl" />
                        </div>
                        <div>
                          <h3 className="font-bold text-lg text-gray-900">{tab.name}</h3>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {tab.list.length} {tab.list.length === 1 ? 'item' : 'items'} available
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => addSett(tab.addFunc)}
                        className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-950 to-blue-800 text-white rounded-lg hover:shadow-lg transition-all font-medium"
                      >
                        <FaPlus />
                        <span>Add {tab.name.slice(0, -1)}</span>
                      </button>
                    </div>
                  </div>

                  {/* Tab List */}
                  <div className="max-h-96 overflow-y-auto">
                    {tab.list.length > 0 ? (
                      <div className="divide-y divide-gray-200">
                        {tab.list.map((entry, idx) => (
                          <div 
                            key={idx} 
                            className="flex justify-between items-center px-6 py-4 hover:bg-gray-50 transition-colors group"
                          >
                            <div className="flex items-center gap-4 flex-1">
                              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                <span className="text-blue-600 font-semibold text-sm">{idx + 1}</span>
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{entry.name || entry.programName || entry.collection_name || 'N/A'}</p>
                                {(entry.code || entry.programCode || entry.subProgramCode) && (
                                  <p className="text-sm text-gray-500 mt-0.5">
                                    Code: {entry.code || entry.programCode || entry.subProgramCode}
                                  </p>
                                )}
                              </div>
                            </div>
                            <button
                              className="p-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                              title="Delete"
                            >
                              <FaTrash className="text-sm" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-6 py-12 text-center">
                        <Icon className="text-gray-400 text-5xl mx-auto mb-4" />
                        <p className="text-gray-600 font-medium">No {tab.name.toLowerCase()} available</p>
                        <p className="text-gray-500 text-sm mt-2 mb-4">Get started by adding your first {tab.name.slice(0, -1).toLowerCase()}</p>
                        <button
                          onClick={() => addSett(tab.addFunc)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-950 to-blue-800 text-white rounded-lg hover:shadow-lg transition-all font-medium"
                        >
                          <FaPlus />
                          <span>Add {tab.name.slice(0, -1)}</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {modal.open && (
        <Modal title={modal.title} onClose={handleClose}>
          {modal.body}
        </Modal>
      )}
    </div>
  );
}
